import { r } from '@/theme/tokens';
import { Stack } from '@/ui';
import { Box, Checkbox, Popover, Text } from '@/ui/primitives';
import {
  buildCheckboxPopoverOptionRowSx,
  buildFilterPopoverRootSx,
  checkboxPopoverBodySx,
  checkboxPopoverCheckboxSx,
  checkboxPopoverEmptyTextSx,
  checkboxPopoverOptionLabelSx,
  checkboxPopoverPaperSx,
  checkboxPopoverSectionDividerSx,
  checkboxPopoverSectionTitleSx,
} from './filterControlStyles';
import { filterTokens } from './filterTokens';
import type { PopoverProps, SxProps, Theme } from '@/ui/primitives';
import type { ReactElement } from 'react';

type CheckboxPopoverOption = {
  key: string;
  label: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (nextChecked: boolean) => void;
};

type CheckboxPopoverSection = {
  key: string;
  title?: string;
  options: CheckboxPopoverOption[];
  emptyText?: string;
};

type CheckboxPopoverProps = {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  sections: CheckboxPopoverSection[];
  minWidth?: number;
  paperSx?: SxProps<Theme>;
  anchorOrigin?: PopoverProps['anchorOrigin'];
  transformOrigin?: PopoverProps['transformOrigin'];
};

export const CheckboxPopover = ({
  open,
  anchorEl,
  onClose,
  sections,
  minWidth,
  paperSx,
  anchorOrigin = { vertical: 'bottom', horizontal: 'right' },
  transformOrigin = { vertical: 'top', horizontal: 'right' },
}: CheckboxPopoverProps): ReactElement => {
  const optionRowMinHeight = `${filterTokens.optionRowMinHeight}px`;
  const resolvedPaperSx = [
    checkboxPopoverPaperSx,
    minWidth !== undefined ? { minWidth } : null,
    paperSx ?? null,
  ].filter(Boolean) as SxProps<Theme>;

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={anchorOrigin}
      transformOrigin={transformOrigin}
      sx={buildFilterPopoverRootSx(r('md'))}
      paperSx={resolvedPaperSx}
      slotProps={{
        paper: {
          square: false,
          elevation: 0,
        },
      }}
    >
      <Stack gap="none">
        <Stack gap="none" sx={checkboxPopoverBodySx}>
          {sections.map((section, sectionIndex) => (
            <Stack key={section.key} gap="none">
              {sectionIndex > 0 ? <Box sx={checkboxPopoverSectionDividerSx} /> : null}
              {section.title ? (
                <Text role="label" color="textSecondary" sx={checkboxPopoverSectionTitleSx}>
                  {section.title}
                </Text>
              ) : null}
              {section.options.length > 0 ? (
                section.options.map((option) => (
                  <Stack
                    key={option.key}
                    direction="row"
                    alignItems="center"
                    gap="sm"
                    role="button"
                    aria-disabled={option.disabled ? 'true' : undefined}
                    tabIndex={option.disabled ? -1 : 0}
                    onClick={() => {
                      if (option.disabled) return;
                      option.onChange(!option.checked);
                    }}
                    onKeyDown={(event) => {
                      if (option.disabled) return;
                      if (event.key !== 'Enter' && event.key !== ' ') return;
                      event.preventDefault();
                      option.onChange(!option.checked);
                    }}
                    sx={buildCheckboxPopoverOptionRowSx({
                      disabled: option.disabled === true,
                      minHeight: optionRowMinHeight,
                    })}
                  >
                    <Checkbox
                      size="small"
                      checked={option.checked}
                      disabled={option.disabled}
                      onClick={(event) => {
                        event.stopPropagation();
                      }}
                      onChange={(event) => {
                        option.onChange(event.target.checked);
                      }}
                      sx={checkboxPopoverCheckboxSx}
                    />
                    <Text role="body" sx={checkboxPopoverOptionLabelSx}>
                      {option.label}
                    </Text>
                  </Stack>
                ))
              ) : section.emptyText ? (
                <Text role="body" color="textSecondary" sx={checkboxPopoverEmptyTextSx}>
                  {section.emptyText}
                </Text>
              ) : null}
            </Stack>
          ))}
        </Stack>
      </Stack>
    </Popover>
  );
};

export type { CheckboxPopoverOption, CheckboxPopoverProps, CheckboxPopoverSection };
